import axios, { InternalAxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import config from './config';
import { TResponse } from '../types/common';
import { getToken } from './utils';
import { useUserStore } from '../store/slices/user';

const axiosInstance = axios.create({
    baseURL: config.apiUrl
});

axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        config.headers.authorization = "Bearer " + getToken();
            // to bypass ngrok warning
            // config.headers["ngrok-skip-browser-warning"] = "2525"
            return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      
        return response;
    },
    (error: AxiosError) => {
        let response: TResponse<any> = {
            success: false,
            message: "Something went wrong",
            code: 500,
            data: null
        };
        if (error.response?.data) {
            const data = error.response.data as Partial<TResponse>;
            response.message = data.message ?? error.message;
            if (Array.isArray(data.errors) && data.errors.length > 0) {
                response.message = data.errors[0].msg;
                response.errors = data.errors;
            }
        } else {
            response.message = error.message;
        }

        // A 401 here always means the stored token is gone/expired/revoked
        // (see Auth.validateTokenAdmin - INVALID_TOKEN/UNAUTHORIZE both use
        // 401), never a permissions decision (those come back as 403, e.g.
        // requireSuperAdmin/requirePermission). Previously this surfaced
        // whatever raw backend text came back with no indication of what
        // to do next and no forced re-login, so a stale session just looked
        // like the page had "denied access" for no reason (Peter, Sept
        // 2026 - reported for the Admin Users phone field specifically,
        // but this applied to every write call once a session went stale).
        //
        // Peter, Oct 2026: "I clicked Save Sculpture and when I went back
        // to continue everything had been deleted" - traced to this same
        // logout() firing from one of the two background polls that run
        // continuously the whole time the admin app is open regardless of
        // what the admin is doing (notification unread-count every 15s,
        // dashboard stats every 60s - see their own hooks). Any single
        // transient 401 on either of those (a brief backend blip/restart
        // during a deploy, a connection hiccup - never something the admin
        // did) wiped the token for the ENTIRE app mid-edit, not just that
        // one background request, so the next real action (Save) silently
        // had no valid token either and the edit never actually reached
        // the server. A stale session should still force a real re-login
        // for an action the admin actually took (the Sept 2026 case this
        // was built for), just not be triggered by a side request the
        // admin never initiated and may not even be looking at.
        const isBackgroundPoll =
            typeof error.config?.url === "string" &&
            (error.config.url.includes("/notification/unread-count") ||
                error.config.url.includes("/dashboard/stats"));

        if (error.response?.status === 401 && !isBackgroundPoll) {
            response.message = "Your session has expired. Please log in again.";
            useUserStore.getState().logout();
        }

        return Promise.reject(response);
    },
);

export default axiosInstance;